import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUp, ArrowRight } from "lucide-react";
import { UBot } from "../components/UBot";
import { PrimaryButton } from "../components/bits";
import * as store from "../lib/storage";
import { SUPPORT_ENABLED } from "../lib/features";
import { WinnersBanner } from "../components/winners/WinnersBanner";

const COACH = [
  { Icon: ArrowLeft, color: "text-coral-500", title: "Swipe left", body: "Not for you? Skip to the next project." },
  { Icon: ArrowUp, color: "text-sky-500", title: "Swipe up", body: "Curious? Open the full story, demo and video." },
  SUPPORT_ENABLED
    ? { Icon: ArrowRight, color: "text-mint-500", title: "Swipe right", body: "Love it? Pledge support, offer help or send the team a message." }
    : { Icon: ArrowRight, color: "text-mint-500", title: "Swipe right", body: "Love it? Save it to your favourites to revisit anytime." },
];

export default function SplashPage() {
  const navigate = useNavigate();
  const [step, setStep] = React.useState(-1); // -1 = intro, 0..2 coach

  React.useEffect(() => { if (store.splashDone()) navigate("/deck", { replace: true }); }, [navigate]);

  const start = () => { store.markSplashDone(); navigate("/deck"); };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12 text-center" data-testid="splash">
      <AnimatePresence mode="wait">
        {step === -1 ? (
          <motion.div key="intro" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="flex flex-col items-center">
            <div className="animate-ubot-bounce"><UBot size={140} waving /></div>
            <p className="mt-4 text-[12px] font-bold tracking-widest text-gold-400">U-HACKATHON 2026 · BANGKOK</p>
            <h1 className="mt-2 max-w-md font-display text-[40px] font-extrabold leading-tight text-white">99 projects. One swipe at a time.</h1>
            <p className="mt-3 max-w-sm text-on-dark-muted">Meet the builders of U-Hackathon 2026, then cheer them on, learn their story or lend a hand.</p>
            <div className="mt-7 flex flex-col items-center gap-3">
              <PrimaryButton onClick={() => setStep(0)} data-testid="splash-start">Start swiping</PrimaryButton>
              <button onClick={start} className="font-semibold text-violet-300" data-testid="splash-browse">Browse the list instead</button>
            </div>
            <WinnersBanner className="mt-8 w-full max-w-sm" />
          </motion.div>
        ) : (
          <motion.div key={`coach-${step}`} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="flex w-full max-w-sm flex-col items-center">
            <div className={`flex h-24 w-24 items-center justify-center rounded-full bg-white/10 ${COACH[step].color}`}>
              {React.createElement(COACH[step].Icon, { size: 52 })}
            </div>
            <h2 className="mt-6 font-display text-[30px] font-extrabold text-white">{COACH[step].title}</h2>
            <p className="mt-2 text-[16px] text-on-dark-muted">{COACH[step].body}</p>
            <div className="mt-6 flex items-center gap-2">
              {COACH.map((_, i) => <span key={i} className={`h-2 rounded-full transition-all ${i === step ? "w-6 bg-gold-500" : "w-2 bg-white/20"}`} />)}
            </div>
            <div className="mt-7 flex items-center gap-4">
              <button onClick={start} className="font-semibold text-on-dark-muted" data-testid="coach-skip">Skip intro</button>
              <PrimaryButton onClick={() => (step < 2 ? setStep(step + 1) : start())} data-testid="coach-next">
                {step < 2 ? "Next" : "Got it, let's go!"}
              </PrimaryButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
